import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can } from '@/shared/screen/define';
import type { AnyScreenAction, ScreenManifest } from '@/shared/screen';

import { billingRowAccess } from './billing.access';
import { BILLING_PATH } from './billing.search';
import type { Billing } from './billing.wire';

const emptyInput = z.strictObject({});
const preview = () => ({
  title: t`Confirmar operação da cobrança`,
  detail: t`Confira os dados e confirme na tela. Nenhuma operação será enviada sem confirmação.`,
});

export const billingCreate = action({
  id: 'billing.create',
  kind: 'write',
  label: msg`Nova cobrança`,
  description: msg`Abre o formulário de Nova cobrança: carteira, pagador, valor, vencimento, categorias e forma de cobrança. Nada é criado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'new', draft: undefined }),
});
export const billingRegister = action({
  id: 'billing.register',
  kind: 'write',
  label: msg`Registrar cobrança`,
  description: msg`Abre o registro da cobrança aberta na tela, ainda não registrada, com o resumo para conferir. Nada é registrado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'register' }),
});
export const billingImport = action({
  id: 'billing.import',
  kind: 'write',
  label: msg`Importar CNAB`,
  description: msg`Abre a importação de um arquivo CNAB de cobranças, com nome e validação opcional do arquivo. Nada é importado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'import' }),
});
export const billingSave = action({
  id: 'billing.save',
  kind: 'write',
  label: msg`Alterar cobrança`,
  description: msg`Abre a alteração da cobrança aberta na tela (pagador, documento, valor, vencimento, desconto e categorias), só enquanto ela não está registrada. Nada é salvo sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'edit' }),
});
export const billingCancel = action({
  id: 'billing.cancel',
  kind: 'destructive',
  label: msg`Solicitar baixa da cobrança`,
  description: msg`Abre a solicitação de baixa da cobrança aberta na tela, com a opção de informar que ela já foi paga. Nada é enviado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'cancel' }),
});
export const billingEmail = action({
  id: 'billing.email',
  kind: 'write',
  label: msg`Enviar e-mail`,
  description: msg`Abre o envio por e-mail da cobrança aberta na tela ao pagador. Nada é enviado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'email' }),
});
export const billingCommunication = action({
  id: 'billing.communication',
  permission: can('BillingAccount', 'Update'),
  kind: 'write',
  label: msg`Atualizar régua de comunicação`,
  description: msg`Abre a troca da régua de comunicação da cobrança aberta na tela, enquanto ela não está registrada. Nada muda sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'communication' }),
});
export const billingComment = action({
  id: 'billing.comment',
  kind: 'write',
  label: msg`Deixar comentário`,
  description: msg`Abre os comentários da cobrança aberta na tela, para ler e registrar um novo. Nada é registrado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'comment' }),
});
export const billingFees = action({
  id: 'billing.fees',
  permission: can('BillingAccount', 'Create'),
  kind: 'write',
  label: msg`Emolumento`,
  description: msg`Abre o emolumento da cobrança aberta na tela, com os valores a conferir antes de debitar a taxa. Nada é debitado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'fees' }),
});
export const billingBatchCancel = action({
  id: 'billing.batchCancel',
  kind: 'destructive',
  label: msg`Solicitar baixa nesta página`,
  description: msg`Abre a solicitação de baixa das cobranças selecionadas nesta página, uma por vez, só quando todas estão registradas e sem liquidação. Nada é enviado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'batch' }),
});

/** As ações com efeito da lista: criar, importar e a baixa das selecionadas. */
export const billingListEffects = [billingCreate, billingImport, billingBatchCancel];

/** As ações com efeito da cobrança aberta. */
export const billingDetailEffects = [
  billingRegister,
  billingSave,
  billingCancel,
  billingEmail,
  billingCommunication,
  billingComment,
  billingFees,
];

export const billingEffects = [...billingListEffects, ...billingDetailEffects];

const detailBindings = [
  [billingRegister, 'register'],
  [billingSave, 'edit'],
  [billingCancel, 'cancel'],
  [billingEmail, 'email'],
  [billingCommunication, 'communication'],
  [billingComment, 'comment'],
  [billingFees, 'fees'],
] as const;

/** Agent actions are bound to the loaded row and only exist when the screen gate allows them. */
export function billingDetailAgentScreen(
  screen: ScreenManifest,
  row: Billing | undefined,
  access: ReturnType<typeof billingRowAccess> | undefined,
): ScreenManifest {
  if (!row || !access) return { ...screen, actions: [] };
  const allowed: Record<(typeof detailBindings)[number][0]['id'], boolean> = {
    [billingRegister.id]: access.register,
    [billingSave.id]: access.edit,
    [billingCancel.id]: access.cancel,
    [billingEmail.id]: access.email,
    [billingCommunication.id]: access.communication,
    [billingComment.id]: access.comment,
    [billingFees.id]: access.fees,
  };
  const actions: AnyScreenAction[] = [];
  for (const [definition, sheet] of detailBindings) {
    if (!allowed[definition.id]) continue;
    actions.push({
      ...definition,
      kind: definition.id === billingCancel.id ? 'destructive' : 'write',
      input: emptyInput,
      preview: () => ({
        title: t`Confirmar ação na cobrança`,
        detail: t`A ação ficará vinculada à cobrança aberta, com status ${row.statusDisplay ?? row.statusValue}.`,
      }),
      run: ({ nav }) => nav.to(`${BILLING_PATH}/${encodeURIComponent(row.id)}`, { search: { sheet } }),
    });
  }
  return { ...screen, actions };
}

/** Navegação da lista sem alvo de linha: paginar e filtrar (U56), com o `run` do manifesto. */
const LIST_NAVIGATION: ReadonlySet<string> = new Set(['billing.page', 'billing.filter']);

/** List agent can only page/filter, open creation/import and the batch dialog for the current-page selection. */
export function billingListAgentScreen(
  screen: ScreenManifest,
  selectedRows: readonly Billing[],
  canBatchCancel: boolean,
): ScreenManifest {
  const actions: AnyScreenAction[] = [
    ...screen.actions.filter((action) => LIST_NAVIGATION.has(action.id)),
    { ...billingCreate, input: emptyInput },
    { ...billingImport, input: emptyInput },
  ];
  if (selectedRows.length > 0 && canBatchCancel) {
    actions.push({
      ...billingBatchCancel,
      kind: 'destructive',
      input: emptyInput,
      preview: () => ({
        title: t`Solicitar baixa de ${selectedRows.length} cobranças selecionadas`,
        detail: t`Somente cobranças elegíveis selecionadas nesta página serão processadas, uma por vez.`,
        tone: 'destructive',
      }),
      run: ({ nav }) => nav.search({ sheet: 'batch' }),
    });
  }
  return { ...screen, actions };
}
